import React, {useReducer, useState} from 'react';
import ReactDOMServer from 'react-dom/server';
import PropTypes from 'prop-types';
import {Grid} from '@material-ui/core';
import {startCase} from 'lodash/string';
import {isEmpty} from 'lodash/lang';
import Checkbox from './Checkbox';
import {INSTRUMENT_MAP, INSTRUMENT_NAME_MAP, downloadCanvas, getCurrentDate, sleep, isSafari, isIOS} from './utils';

const CANVAS_WIDTH = 893;
const SMALL_CANVAS_WIDTH = 470;
const CANVAS_MARGIN_TOP = 5;
const CANVAS_MARGIN_BOTTOM = 5;
const CANVAS_MARGIN_LEFT = 10;
const CANVAS_MARGIN_RIGHT = 10;
const CANVAS_CONTENT_WIDTH = CANVAS_WIDTH - CANVAS_MARGIN_LEFT - CANVAS_MARGIN_RIGHT;
const WATERMARK_SIZE = 100;

export const getAdditionalTableWidth = (settingList, attributeList) => {
  let additionalTableLength = 0;
  settingList.forEach((obj) => {
    attributeList.forEach((attr) => {
      if (obj[attr]) {
        additionalTableLength += obj[attr].length * 7;
      }
    });
  });
  return additionalTableLength;
};

const createCanvas = ({width = CANVAS_WIDTH, height}) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  return {canvas, ctx};
};

const drawImage = ({blob, ctx, x, y, width, height}) =>
  new Promise((resolve) => {
    const img = new Image();
    img.width = width;
    img.crossOrigin = 'Anonymous';
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      ctx.drawImage(img, x, y, width, height);
      URL.revokeObjectURL(url);
      resolve();
    };
    img.src = url;
  });

const drawElement = ({node, ctx, x = CANVAS_MARGIN_LEFT, y = 0, width = CANVAS_CONTENT_WIDTH, height}) =>
  new Promise((resolve) => {
    const tableBorderColor = '#828790';
    const svgHtml = ReactDOMServer.renderToStaticMarkup(
      <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height}>
        <style
          dangerouslySetInnerHTML={{
            __html: `
          * {
            font-family: 'Lato', sans-serif;
            font-size: 14px;
            color: #4d4d4d;
          }
          table {
            margin: 10px 0;
            text-align: left;
            vertical-align: middle;
            border-collapse: collapse;
            white-space: nowrap;
          }
          table.full-width {
            width: 100%;
          }
          table, th, td {
            border: 1px solid ${tableBorderColor};
          }
          th, td {
            height: 24px;
            border: 2px solid ${tableBorderColor};
            padding: 2px 4px;
          }
          th {
            background-color: #dfe0df;
          }
          th.header {
            height: 32px;
          }
          td.highlight {
            background-color: #757575;
            color: #fff;
            font-size: 24px;
          }
          td.text-center {
            text-align: center;
          }
          .width-50 {
            width: 50%;
          }
          .width-33 {
            width: 33.33%;
          }
        `,
          }}
        />
        <foreignObject width="100%" height="100%">
          <div xmlns="http://www.w3.org/1999/xhtml">{node}</div>
        </foreignObject>
      </svg>,
    );

    const svgBlob = new Blob([svgHtml], {type: 'image/svg+xml'});
    const reader = new FileReader();
    reader.onload = ({target: {result: dataUrl}}) => {
      const img = new Image();
      img.width = width;
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        ctx.drawImage(img, x, y, width, height);
        resolve();
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(svgBlob);
  });

const drawWaterMark = ({dateStr, timeStr, ctx, x, y}) =>
  new Promise((resolve) => {
    const svgHtml = ReactDOMServer.renderToStaticMarkup(
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100" opacity="0.95">
        <defs>
          <style
            dangerouslySetInnerHTML={{
              __html:
                '.cls-1{isolation:isolate;}.cls-2{fill:#0560a4;}.cls-2,.cls-3{opacity:0.75;mix-blend-mode:multiply;}.cls-4,.cls-5,.cls-6{fill:#4d74b0;}.cls-4{opacity:0.17;}.cls-5{opacity:0.25;}.cls-6{opacity:0.3;}',
            }}
          />
        </defs>
        <g className="cls-1">
          <g id="Layer_1" data-name="Layer 1">
            <rect className="cls-2" width="100" height="100" />
            <g className="cls-3">
              <rect className="cls-4" y="53.76344" width="12.5" height="46.23656" />
              <rect className="cls-4" x="12.5" y="24.73118" width="12.5" height="75.26882" />
              <rect className="cls-5" x="25" width="12.5" height="100" />
              <rect className="cls-4" x="37.5" y="32.25806" width="12.5" height="67.74194" />
              <rect className="cls-4" x="50" y="65.5914" width="12.5" height="34.4086" />
              <rect className="cls-6" x="75" y="41.93548" width="12.5" height="58.06452" />
              <rect className="cls-4" x="87.5" y="62.36559" width="12.5" height="37.63441" />
            </g>
          </g>
        </g>
        <foreignObject width="100%" height="100%">
          <div xmlns="http://www.w3.org/1999/xhtml">
            <div
              style={{
                paddingTop: 27,
                paddingLeft: 7,
                fontSize: 16,
                color: '#fff',
              }}
            >
              <div>{dateStr}</div>
              <div>{timeStr}</div>
            </div>
          </div>
        </foreignObject>
      </svg>,
    );

    const svgBlob = new Blob([svgHtml], {type: 'image/svg+xml'});
    const reader = new FileReader();
    reader.onload = ({target: {result: dataUrl}}) => {
      const img = new Image();
      img.width = WATERMARK_SIZE;
      img.height = WATERMARK_SIZE;
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        ctx.drawImage(img, x, y, WATERMARK_SIZE, WATERMARK_SIZE);
        resolve();
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(svgBlob);
  });

const createFgenSnapshot = async ({channel0Settings, channel1Settings}, dateStr, timeStr) => {
  const staticCount = [channel0Settings.mode, channel1Settings.mode].filter((p_mode) => p_mode === 'Static').length;
  const sweepCount = [channel0Settings.mode, channel1Settings.mode].filter((p_mode) => p_mode === 'Sweep').length;
  const customCount = [channel0Settings.mode, channel1Settings.mode].filter((p_mode) => p_mode === 'Custom').length;

  const isNotViewMode = channel0Settings.graph !== null && channel1Settings.graph !== null;
  const inactiveTableHeight = 110;
  const sweepTableHeight = 360;
  const staticTableHeight = 270;
  const customTableHeight = 300;
  const graphHeight = isNotViewMode ? 500 : 0;
  const titleHeight = 30;

  const settingsArray = [channel0Settings, channel1Settings];
  const canvasHeight =
    staticCount * staticTableHeight +
    sweepCount * sweepTableHeight +
    customCount * (customTableHeight + graphHeight + titleHeight) +
    (settingsArray.length - sweepCount - customCount - staticCount) * inactiveTableHeight +
    CANVAS_MARGIN_TOP +
    CANVAS_MARGIN_BOTTOM;
  const additionalChannel0TableLength = Math.max(getAdditionalTableWidth([channel0Settings], ['sourceFile']), getAdditionalTableWidth([channel0Settings], ['sourceChannel']));
  const additionalChannel1TableLength = Math.max(getAdditionalTableWidth([channel1Settings], ['sourceFile']), getAdditionalTableWidth([channel1Settings], ['sourceChannel']));
  const canvasWidth = SMALL_CANVAS_WIDTH + Math.max(additionalChannel0TableLength, additionalChannel1TableLength);
  const {canvas, ctx} = createCanvas({width: canvasWidth, height: canvasHeight});

  let currentY = CANVAS_MARGIN_TOP;
  for (let index = 0; index < settingsArray.length; ++index) {
    const {mode, graph, ...settings} = settingsArray[index];
    const tableHeight =
      mode === 'Sweep'
        ? sweepTableHeight
        : mode === 'Static'
        ? staticTableHeight
        : mode === 'Custom'
        ? customTableHeight
        : inactiveTableHeight;

    if (mode === 'Custom' && isNotViewMode) {
      await drawImage({
        blob: graph,
        ctx,
        x: CANVAS_MARGIN_LEFT,
        y: currentY,
        width: 400,
        height: graphHeight,
      });

      await drawElement({
        node: (
          <>
            <h1>Graph: Channel {index + 1}</h1>
          </>
        ),
        ctx,
        x: CANVAS_MARGIN_LEFT + 20,
        y: currentY - 10,
        width: 370,
        height: titleHeight,
      });

      currentY += graphHeight;
    }

    await drawElement({
      node: (
        <>
          <table style={{marginBottom: 24}}>
            <thead>
              <tr>
                <th {...{colspan: 2, className: 'header'}}>Channel {parseInt(index) + 1}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Mode</td>
                <td>{mode}</td>
              </tr>
              {Object.entries(settings).map(([key, value]) => (
                <tr key={key}>
                  <td>{startCase(key)}</td>
                  <td>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ),
      ctx,
      x: CANVAS_MARGIN_LEFT,
      y: currentY,
      width: canvasWidth,
      height: tableHeight,
    });

    currentY += tableHeight;
  }

  await drawWaterMark({
    dateStr,
    timeStr,
    ctx,
    x: canvasWidth - WATERMARK_SIZE,
    y: canvasHeight - CANVAS_MARGIN_BOTTOM - WATERMARK_SIZE,
  });

  return {canvas, canvasHeight, name: 'fgen'};
};

const createDmmSnapshot = async ({mode, result, rangeValue}, dateStr, timeStr) => {
  const canvasHeight = 300;
  const {canvas, ctx} = createCanvas({width: SMALL_CANVAS_WIDTH, height: canvasHeight});

  await drawElement({
    node: (
      <>
        <table className="full-width" style={{marginBottom: 24}}>
          <thead>
            <tr>
              <th {...{colspan: 2, className: 'header'}}>Measurements</th>
            </tr>
            <tr>
              <th className="width-50">Type</th>
              <th className="width-50">Value</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{height: 44}}>
              <td className="width-50">{mode}</td>
              <td className="highlight text-center width-50">{result}</td>
            </tr>
          </tbody>
        </table>
        <table className="full-width">
          <thead>
            <tr>
              <th className="width-50">Settings</th>
              <th className="width-50">Value</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="width-50">Measurements Mode</td>
              <td className="width-50">{mode}</td>
            </tr>
            <tr>
              <td className="width-50">Range</td>
              <td className="width-50">{rangeValue}</td>
            </tr>
          </tbody>
        </table>
      </>
    ),
    ctx,
    x: 40,
    y: CANVAS_MARGIN_TOP,
    width: 370,
    height: canvasHeight - CANVAS_MARGIN_TOP - CANVAS_MARGIN_BOTTOM,
  });
  await drawWaterMark({
    dateStr,
    timeStr,
    ctx,
    x: 410 - WATERMARK_SIZE,
    y: canvasHeight - CANVAS_MARGIN_BOTTOM - WATERMARK_SIZE,
  });

  return {canvas, canvasHeight, name: 'dmm'};
};

const createVpsSnapshot = async ({positive, negative}, dateStr, timeStr) => {
  const positiveData = {
    settings: positive.settings,
    readBackValues: positive.readBackValues || {},
  };
  const negativeData = {
    settings: negative.settings,
    readBackValues: negative.readBackValues || {},
  };
  const hasPositiveValue = !isEmpty(positiveData.readBackValues);
  const hasNegativeValue = !isEmpty(negativeData.readBackValues);
  const staticTableHeight = 130;
  const sweepTableHeight = 250;
  const inactiveTableHeight = 70;
  let canvasHeight = 180 + (hasPositiveValue ? 130 : 0) + (hasNegativeValue ? 130 : 0);
  switch (positiveData.settings.mode) {
    case 'Static':
      canvasHeight += staticTableHeight;
      break;
    case 'Sweep':
      canvasHeight += sweepTableHeight;
      break;
    default:
      canvasHeight += inactiveTableHeight;
  }
  switch (negativeData.settings.mode) {
    case 'Static':
      canvasHeight += staticTableHeight;
      break;
    case 'Sweep':
      canvasHeight += sweepTableHeight;
      break;
    default:
      canvasHeight += inactiveTableHeight;
  }
  const {canvas, ctx} = createCanvas({width: SMALL_CANVAS_WIDTH, height: canvasHeight});

  await drawElement({
    node: (
      <>
        <table className="full-width" style={{marginBottom: 24}}>
          <thead>
            <tr>
              <th {...{colspan: 3, className: 'header'}}>Measurements</th>
            </tr>
            <tr>
              <th className="width-33">Channel</th>
              <th className="width-33">Measurement</th>
              <th className="width-33">Value</th>
            </tr>
          </thead>
          <tbody>
            {hasPositiveValue && (
              <>
                <tr style={{height: 44}}>
                  <td className="width-33 text-center" {...{rowSpan: 3}}>
                    Power Supply "+"
                  </td>
                  <td className="width-33">Current</td>
                  <td className="highlight text-center width-33">{positiveData.readBackValues.currentValue}</td>
                </tr>
                <tr style={{height: 44}}>
                  <td className="width-33">Voltage</td>
                  <td className="highlight text-center width-33">{positiveData.readBackValues.voltageValue}</td>
                </tr>
                <tr style={{height: 44}}>
                  <td className="width-33">Power</td>
                  <td className="highlight text-center width-33">{positiveData.readBackValues.powerValue}</td>
                </tr>
              </>
            )}
            {hasNegativeValue && (
              <>
                <tr style={{height: 44}}>
                  <td className="width-33 text-center" {...{rowSpan: 3}}>
                    Power Supply "-"
                  </td>
                  <td className="width-33">Current</td>
                  <td className="highlight text-center width-33">{negativeData.readBackValues.currentValue}</td>
                </tr>
                <tr style={{height: 44}}>
                  <td className="width-33">Voltage</td>
                  <td className="highlight text-center width-33">{negativeData.readBackValues.voltageValue}</td>
                </tr>
                <tr style={{height: 44}}>
                  <td className="width-33">Power</td>
                  <td className="highlight text-center width-33">{negativeData.readBackValues.powerValue}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
        <table className={{marginBotton: 24}}>
          <tr>
            <th className="width-50">Power Supply "+" Settings</th>
            <th className="width-50">Power Supply "+" Values</th>
          </tr>
          <tbody>
            {Object.entries(positiveData.settings).map(([key, value]) => (
              <tr key={key}>
                <td>{startCase(key)}</td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table>
          <tr>
            <th className="width-50">Power Supply "-" Settings</th>
            <th className="width-50">Power Supply "-" Values</th>
          </tr>
          <tbody>
            {Object.entries(negativeData.settings).map(([key, value]) => (
              <tr key={key}>
                <td>{startCase(key)}</td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    ),
    ctx,
    x: 35,
    y: CANVAS_MARGIN_TOP,
    width: 400,
    height: canvasHeight - CANVAS_MARGIN_TOP - CANVAS_MARGIN_BOTTOM,
  });
  await drawWaterMark({
    dateStr,
    timeStr,
    ctx,
    x: 435 - WATERMARK_SIZE,
    y: canvasHeight - CANVAS_MARGIN_BOTTOM - WATERMARK_SIZE,
  });

  return {canvas, canvasHeight, name: 'vps'};
};

const createBodeSnapshot = async ({graph, settings}, dateStr, timeStr) => {
  const {responseList, referenceList, ...restSettings} = settings;
  const additionalTableLength = getAdditionalTableWidth(referenceList, ['sourceFileName', 'channel']); // If td value is too long then we can extend original canvas length
  const graphHeight = graph.height;
  const settingsHeight = 800;
  const canvasHeight = graphHeight + settingsHeight;
  const canvasWidth = (graph.width > CANVAS_WIDTH ? graph.width : CANVAS_WIDTH) + additionalTableLength;
  const {canvas, ctx} = createCanvas({width: canvasWidth, height: canvasHeight});

  await drawImage({
    blob: graph.blob,
    ctx,
    x: CANVAS_MARGIN_LEFT,
    y: CANVAS_MARGIN_TOP,
    width: graph.width,
    height: graphHeight,
  });
  await drawWaterMark({
    dateStr,
    timeStr,
    ctx,
    x: graph.width - CANVAS_MARGIN_RIGHT - WATERMARK_SIZE,
    y: graphHeight - WATERMARK_SIZE,
  });

  await drawElement({
    node: (
      <>
        <table style={{marginBottom: 24}}>
          <thead>
            <tr>
              <th {...{colspan: 2, className: 'header'}}>Stimulus Channel</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(restSettings).map(([key, value]) => (
              <tr key={key}>
                <td>{startCase(key)}</td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table style={{marginBottom: 24}}>
          <thead>
            <tr>
              <th {...{colspan: 3, className: 'header'}}>Response Channels</th>
            </tr>
            <tr>
              <th>Response Name</th>
              <th>State</th>
              <th>Channel Label</th>
            </tr>
          </thead>
          <tbody>
            {responseList.map(({responseName, state, channelLabel}, idx) => (
              <tr key={idx}>
                <td>{responseName}</td>
                <td>{state}</td>
                <td>{channelLabel}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table style={{marginBottom: 24}}>
          <thead>
            <tr>
              <th {...{colspan: 5, className: 'header'}}>Reference Channels</th>
            </tr>
            <tr>
              <th>Reference Name</th>
              <th>State</th>
              <th>Mode</th>
              <th>Source File Name</th>
              <th>Channel</th>
            </tr>
          </thead>
          <tbody>
            {referenceList.map(({referenceName, state, mode, sourceFileName, channel}, idx) => (
              <tr key={idx}>
                <td>{referenceName}</td>
                <td>{state}</td>
                <td>{mode}</td>
                <td>{sourceFileName}</td>
                <td>{channel}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    ),
    ctx,
    y: CANVAS_MARGIN_TOP + graphHeight,
    width: canvasWidth,
    height: settingsHeight,
  });

  return {canvas, canvasHeight, name: 'bode'};
};

const createIvSnapshot = async ({graph, settings}, dateStr, timeStr) => {
  const graphHeight = graph.height;
  const settingsHeight = 400;
  const canvasHeight = graphHeight + settingsHeight;
  const canvasWidth = graph.width > CANVAS_WIDTH ? graph.width : CANVAS_WIDTH;
  const {canvas, ctx} = createCanvas({width: canvasWidth, height: canvasHeight});

  await drawImage({
    blob: graph.blob,
    ctx,
    x: CANVAS_MARGIN_LEFT,
    y: CANVAS_MARGIN_TOP,
    width: graph.width,
    height: graphHeight,
  });
  await drawWaterMark({
    dateStr,
    timeStr,
    ctx,
    x: graph.width - CANVAS_MARGIN_RIGHT - WATERMARK_SIZE,
    y: graphHeight - WATERMARK_SIZE,
  });

  await drawElement({
    node: (
      <>
        <table>
          <thead>
            <tr>
              <th {...{colspan: 2, className: 'header'}}>Settings</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(settings).map(([key, value]) => (
              <tr key={key}>
                <td>{startCase(key)}</td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    ),
    ctx,
    y: CANVAS_MARGIN_TOP + graphHeight,
    height: settingsHeight,
  });

  return {canvas, canvasHeight, name: 'iv'};
};

const createScopeSnapshot = async ({timeGraph, fftGraph, settings, channelMeasurementsData}, dateStr, timeStr) => {
  const {additionalChannels, advancedTriggerSettings, channels, ...restSettings} = settings;
  const additionalRefChannelSimulationTableLength = getAdditionalTableWidth(additionalChannels.referenceChannels.simulateMode, ['sourceCircuit', 'sourceProbe']);
  const additionalRefChannelLoadingTableLength =  getAdditionalTableWidth( additionalChannels.referenceChannels.loadingMode, ['sourceFileName', 'sourceFileChannel']);
  const additionalTableLength = Math.max(additionalRefChannelSimulationTableLength, additionalRefChannelLoadingTableLength); // If td value is too long then we can extend original canvas length
  const settingsHeight = 2000;
  const hasFftGraph = !!fftGraph;
  const graphHeight = timeGraph.height;
  const fftGraphHeight = hasFftGraph ? fftGraph.height : 0;
  const canvasHeight = settingsHeight + graphHeight + fftGraphHeight;
  const canvasWidth = (timeGraph.width > CANVAS_WIDTH ? timeGraph.width : CANVAS_WIDTH) + additionalTableLength;
  const {canvas, ctx} = createCanvas({width: canvasWidth, height: canvasHeight});

  await drawImage({
    blob: timeGraph.blob,
    ctx,
    x: CANVAS_MARGIN_LEFT,
    y: CANVAS_MARGIN_TOP,
    width: timeGraph.width,
    height: graphHeight,
  });
  await drawWaterMark({
    dateStr,
    timeStr,
    ctx,
    x: timeGraph.width - CANVAS_MARGIN_RIGHT - WATERMARK_SIZE,
    y: graphHeight - WATERMARK_SIZE,
  });

  if (hasFftGraph) {
    await drawImage({
      blob: fftGraph.blob,
      ctx,
      x: CANVAS_MARGIN_LEFT,
      y: CANVAS_MARGIN_TOP + graphHeight,
      width: fftGraph.width,
      height: fftGraphHeight,
    });
  }

  await drawElement({
    node: (
      <>
        <table style={{marginBottom: 24}}>
          <thead>
            <tr>
              <th {...{colspan: 2, className: 'header'}}>General Settings</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(restSettings).map(([key, value]) => (
              <tr key={key}>
                <td>{startCase(key)}</td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table style={{marginBottom: 24}}>
          <thead>
            <tr>
              <th {...{colspan: 2, className: 'header'}}>Advanced Trigger Settings</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(advancedTriggerSettings).map(([key, value]) => (
              <tr key={key}>
                <td>{startCase(key)}</td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table style={{marginBottom: 24}}>
          <thead>
            <tr>
              <th {...{colspan: 7, className: 'header'}}>Channels</th>
            </tr>
            <tr>
              <th>Name</th>
              <th>State</th>
              <th>Coupling</th>
              <th>Probe Attenuation</th>
              <th>Vertical Offset</th>
              <th>Vertical Position</th>
              <th>Volt Per Division</th>
            </tr>
          </thead>
          <tbody>
            {channels.map(
              ({name, coupling, probeAttenuation, state, verticalOffset, verticalPosition, voltPerDivision}) => (
                <tr key={name}>
                  <td>{name}</td>
                  <td>{state}</td>
                  <td>{coupling}</td>
                  <td>{probeAttenuation}</td>
                  <td>{verticalOffset}</td>
                  <td>{verticalPosition}</td>
                  <td>{voltPerDivision}</td>
                </tr>
              ),
            )}
          </tbody>
        </table>
        <table style={{marginBottom: 24}}>
          <thead>
            <tr>
              <th {...{colspan: 7, className: 'header'}}>Reference Channels</th>
            </tr>
            <tr>
              <th>Name</th>
              <th>State</th>
              <th>Reference Mode</th>
              <th>Source File Name</th>
              <th>Source File Channel</th>
              <th>Vertical Position</th>
              <th>Volt Per Division</th>
            </tr>
          </thead>
          <tbody>
            {additionalChannels.referenceChannels.loadingMode.map(
              ({name, state, referenceMode, sourceFileName, sourceFileChannel, verticalPosition, voltPerDivision}) => (
                <tr key={name}>
                  <td>{name}</td>
                  <td>{state}</td>
                  <td>{referenceMode}</td>
                  <td>{sourceFileName}</td>
                  <td>{sourceFileChannel}</td>
                  <td>{verticalPosition}</td>
                  <td>{voltPerDivision}</td>
                </tr>
              ),
            )}
          </tbody>
        </table>
        <table style={{marginBottom: 24}}>
          <thead>
          <tr>
            <th {...{colspan: 8, className: 'header'}}>Reference Channels</th>
          </tr>
          <tr>
            <th>Name</th>
            <th>State</th>
            <th>Reference Mode</th>
            <th>Source Circuit</th>
            <th>Source Probe</th>
            <th>Trigger Source Checked</th>
            <th>Vertical Position</th>
            <th>Volt Per Division</th>
          </tr>
          </thead>
          <tbody>
          {additionalChannels.referenceChannels.simulateMode.map(
            ({name, state, referenceMode, sourceCircuit, sourceProbe, isTriggerSourceSet , verticalPosition, voltPerDivision}) => (
              <tr key={name}>
                <td>{name}</td>
                <td>{state}</td>
                <td>{referenceMode}</td>
                <td>{sourceCircuit}</td>
                <td>{sourceProbe}</td>
                <td>{isTriggerSourceSet}</td>
                <td>{verticalPosition}</td>
                <td>{voltPerDivision}</td>
              </tr>
            ),
          )}
          </tbody>
        </table>
        <table style={{marginBottom: 24}}>
          <thead>
            <tr>
              <th {...{colspan: 2, className: 'header'}}>Additional Channels - FFT</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(additionalChannels.fft).map(([key, value]) => (
              <tr key={key}>
                <td>{startCase(key)}</td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table style={{marginBottom: 24}}>
          <thead>
            <tr>
              <th {...{colspan: 2, className: 'header'}}>Additional Channels - Math</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(additionalChannels.math).map(([key, value]) => (
              <tr key={key}>
                <td>{startCase(key)}</td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table style={{marginBottom: 24}}>
          <thead>
            <tr>
              <th {...{colspan: 5, className: 'header'}}>Channel Measurements Data</th>
            </tr>
            <tr>
              <th>Channel Name</th>
              <th>VPP</th>
              <th>RMS</th>
              <th>Frequency</th>
              <th>Period</th>
            </tr>
          </thead>
          <tbody>
            {channelMeasurementsData.map(({channelIndex, channelName, frequency, period, rms, vpp}) => (
              <tr key={channelName}>
                <td>{channelName}</td>
                <td>{vpp}</td>
                <td>{rms}</td>
                <td>{frequency}</td>
                <td>{period}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    ),
    ctx,
    y: CANVAS_MARGIN_TOP + graphHeight + fftGraphHeight,
    width: canvasWidth,
    height: settingsHeight,
  });

  return {canvas, canvasHeight, name: 'scope'};
};

const createDataLoggerSnapshot = async ({graph, settings}, dateStr, timeStr) => {
  const settingsHeight = settings.channels.length * 35 + 1000;
  const graphHeight = graph.height;
  const canvasHeight = settingsHeight + graphHeight;
  const canvasWidth = graph.width > CANVAS_WIDTH ? graph.width : CANVAS_WIDTH;
  const {canvas, ctx} = createCanvas({width: canvasWidth, height: canvasHeight});

  await drawImage({
    blob: graph.blob,
    ctx,
    x: CANVAS_MARGIN_LEFT,
    y: CANVAS_MARGIN_TOP,
    width: graph.width,
    height: graphHeight,
  });
  await drawWaterMark({
    dateStr,
    timeStr,
    ctx,
    x: graph.width - CANVAS_MARGIN_RIGHT - WATERMARK_SIZE,
    y: graphHeight - WATERMARK_SIZE,
  });

  const {channels, sampleRate, loggingFormat, startTrigger, stopTrigger, loggingStyle, fileName} = settings;
  await drawElement({
    node: (
      <>
        <table style={{marginBottom: 24}}>
          <thead>
            <tr>
              <th {...{colspan: 6, className: 'header'}}>Channels</th>
            </tr>
            <tr>
              <th>Bank</th>
              <th>Label</th>
              <th>Status</th>
              <th>Channel Name</th>
              <th>Vertical Position</th>
              <th>Vertical Range</th>
            </tr>
          </thead>
          <tbody>
            {channels.map(({index, label, bank, channelName, status, verticalPosition, verticalRange}) => (
              <tr key={index}>
                <td>{bank}</td>
                <td>{label}</td>
                <td>{status}</td>
                <td>{channelName}</td>
                <td>{verticalPosition}</td>
                <td>{verticalRange}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table style={{marginBottom: 24}}>
          <thead>
            <tr>
              <th {...{colspan: 2, className: 'header'}}>Sample Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Value</td>
              <td>{sampleRate}</td>
            </tr>
          </tbody>
        </table>
        <table style={{marginBottom: 24}}>
          <thead>
            <tr>
              <th {...{colspan: 7, className: 'header'}}>Start Trigger</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(startTrigger).map(([key, value]) => (
              <tr key={key}>
                <td>{startCase(key)}</td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table style={{marginBottom: 24}}>
          <thead>
            <tr>
              <th {...{colspan: 7, className: 'header'}}>Stop Trigger</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(stopTrigger).map(([key, value]) => (
              <tr key={key}>
                <td>{startCase(key)}</td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table style={{marginBottom: 24}}>
          <thead>
            <tr>
              <th {...{colspan: 2, className: 'header'}}>File Format</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Value</td>
              <td>{loggingFormat}</td>
            </tr>
          </tbody>
        </table>
        <table style={{marginBottom: 24}}>
          <thead>
            <tr>
              <th {...{colspan: 2, className: 'header'}}>File Location</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Value</td>
              <td>{loggingStyle}</td>
            </tr>
          </tbody>
        </table>
        {(loggingStyle === 'USB storage plugged in ELVIS III') && (
          <>
            <table style={{marginBottom: 24}}>
              <thead>
                <tr>
                  <th {...{colspan: 2, className: 'header'}}>File Name</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Value</td>
                  <td>{fileName}</td>
                </tr>
              </tbody>
            </table>
          </>
        )}
      </>
    ),
    ctx,
    y: CANVAS_MARGIN_TOP + graphHeight,
    height: settingsHeight,
  });

  return {canvas, canvasHeight, name: 'datalogger'};
};

const createDataGenSnapshot = async ({a0Settings, a1Settings, b0Settings, b1Settings}, dateStr, timeStr) => {
  const settingsArray = [a0Settings, a1Settings, b0Settings, b1Settings];
  const channelName = ['A/AO0', 'A/AO1', 'B/AO0', 'B/AO1'];
  const singleBarHeight = 30;
  let contentHeight = 0;
  const gapHeight = 30;
  for (let index = 0; index < settingsArray.length; ++index) {
    contentHeight += (Object.keys(settingsArray[index]).length + 1) * singleBarHeight + gapHeight;
  }
  const canvasHeight = contentHeight + CANVAS_MARGIN_TOP + CANVAS_MARGIN_BOTTOM;
  const {canvas, ctx} = createCanvas({width: SMALL_CANVAS_WIDTH, height: canvasHeight});
  let currentY = CANVAS_MARGIN_TOP;
  for (let index = 0; index < settingsArray.length; ++index) {
    const {mode, ...settings} = settingsArray[index];
    const tableHeight = (Object.keys(settingsArray[index]).length + 1) * singleBarHeight + gapHeight;
    await drawElement({
      node: (
        <>
          <table className="full-width" style={{marginBottom: 24}}>
            <thead>
              <tr>
                <th {...{colspan: 2, className: 'header'}}> {channelName[index]}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="width-50">Mode</td>
                <td className="width-50">{mode}</td>
              </tr>
              {Object.entries(settings).map(([key, value]) => (
                <tr key={key}>
                  <td className="width-50">{startCase(key)}</td>
                  <td className="width-50">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ),
      ctx,
      x: CANVAS_MARGIN_LEFT,
      y: currentY,
      width: 370,
      height: tableHeight,
    });
    currentY += tableHeight;
  }

  await drawWaterMark({
    dateStr,
    timeStr,
    ctx,
    x: 410 - WATERMARK_SIZE,
    y: canvasHeight - CANVAS_MARGIN_BOTTOM - WATERMARK_SIZE,
  });
  return {canvas, canvasHeight, name: 'datagen'};
};

const createDigitalIOSnapshot = async (
  {channel0Settings, channel1Settings, channel2Settings, channel3Settings},
  dateStr,
  timeStr,
) => {
  const settingsArray = [channel0Settings, channel1Settings, channel2Settings, channel3Settings];
  const channelName = ['A/DIO7:0', 'A/DIO15:8', 'B/DIO7:0', 'B/DIO15:8'];
  const singleBarHeight = 30;
  let contentHeight = 0;
  const gapHeight = 30;
  for (let index = 0; index < settingsArray.length; ++index) {
    contentHeight += (Object.keys(settingsArray[index]).length + 1) * singleBarHeight + gapHeight;
  }
  const canvasHeight = contentHeight + CANVAS_MARGIN_TOP + CANVAS_MARGIN_BOTTOM;
  const {canvas, ctx} = createCanvas({width: SMALL_CANVAS_WIDTH, height: canvasHeight});
  let currentY = CANVAS_MARGIN_TOP;
  for (let index = 0; index < settingsArray.length; ++index) {
    const {mode, ...settings} = settingsArray[index];
    const tableHeight = (Object.keys(settingsArray[index]).length + 1) * singleBarHeight + gapHeight;
    await drawElement({
      node: (
        <>
          <table className="full-width" style={{marginBottom: 24}}>
            <thead>
              <tr>
                <th {...{colspan: 2, className: 'header'}}>{channelName[index]}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="width-50">Mode</td>
                <td className="width-50">{mode}</td>
              </tr>
              {Object.entries(settings).map(([key, value]) => (
                <tr key={key}>
                  <td className="width-50">{startCase(key)}</td>
                  <td className="width-50">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ),
      ctx,
      x: CANVAS_MARGIN_LEFT,
      y: currentY,
      width: 370,
      height: tableHeight,
    });
    currentY += tableHeight;
  }

  await drawWaterMark({
    dateStr,
    timeStr,
    ctx,
    x: 410 - WATERMARK_SIZE,
    y: canvasHeight - CANVAS_MARGIN_BOTTOM - WATERMARK_SIZE,
  });
  return {canvas, canvasHeight, name: 'digitalio'};
};

export const createSnapshot = (result, instrumentName, dateStr, timeStr) => {
  switch (instrumentName) {
    case 'fgen':
      return createFgenSnapshot(result, dateStr, timeStr);
    case 'dmm':
      return createDmmSnapshot(result, dateStr, timeStr);
    case 'vps':
      return createVpsSnapshot(result, dateStr, timeStr);
    case 'bode':
      return createBodeSnapshot(result, dateStr, timeStr);
    case 'iv':
      return createIvSnapshot(result, dateStr, timeStr);
    case 'scope':
      return createScopeSnapshot(result, dateStr, timeStr);
    case 'datalogger':
      return createDataLoggerSnapshot(result, dateStr, timeStr);
    case 'datagenerator':
      return createDataGenSnapshot(result, dateStr, timeStr);
    case 'digitalio':
      return createDigitalIOSnapshot(result, dateStr, timeStr);
  }
};

const clickInstrumentReducer = (state, action) => {
  let {selectedInstruments} = state;
  let {instrumentName} = action;
  if (selectedInstruments.has(instrumentName)) {
    selectedInstruments.delete(instrumentName);
  } else {
    selectedInstruments.add(instrumentName);
  }
  return {
    ...state,
    selectedInstruments: new Set(selectedInstruments),
  };
};

const snapshotReducer = (state, action) => {
  switch (action.type) {
    case 'CLICK_INSTRUMENT':
      return clickInstrumentReducer(state, action);
    default:
      return state;
  }
};

const useSnapshotReducer = () => {
  const [state, dispatch] = useReducer(snapshotReducer, {
    selectedInstruments: new Set(),
  });

  const clickInstrument = (instrumentName) => dispatch({type: 'CLICK_INSTRUMENT', instrumentName});

  return {
    state,
    clickInstrument,
  };
};

const SnapshotForm = ({instruments, toolbarRef}) => {
  const {
    state: {selectedInstruments},
    clickInstrument,
  } = useSnapshotReducer();

  const [warningMessage, setWarningMessage] = useState('');

  const createSnapshots = async () => {
    if (isIOS) {
      setWarningMessage('iOS does not allow taking screenshots.');
      return;
    }
    const instrumentsWrapper = toolbarRef.current.closest('.bentobox-wrapper').querySelector('.instruments-wrapper');
    const instrumentArr = Array.from(selectedInstruments);
    const {year, month, day, hour, minute, second} = getCurrentDate();
    const dateStr = `${month}/${day}/${year}`;
    const timeStr = `${hour}:${minute}:${second}`;

    for (const instrumentName of instrumentArr) {
      const instrumentEl = instrumentsWrapper.querySelector(INSTRUMENT_MAP[instrumentName]);
      if (instrumentEl && instrumentEl.getMeasurementsData) {
        const {name, canvas} = await Promise.all([instrumentEl.getMeasurementsData()]).then(([result]) => {
          return createSnapshot(result, instrumentName, dateStr, timeStr);
        });

        const fileName = `${name}_${year}${month}${day}_${hour}${minute}${second}.png`;
        if (isSafari) {
          await downloadCanvas(canvas, fileName);
          await sleep(1000);
        } else {
          downloadCanvas(canvas, fileName);
        }
      }
    }
  };

  return (
    <Grid container spacing={16} direction="column" className="snapshot-instruments">
      <Grid item xs={12} className="title">
        Choose instrument(s) to capture
      </Grid>
      {[...instruments.large, ...instruments.small].map(({name}) => {
        const isSelected = selectedInstruments.has(name);
        return (
          <Grid
            key={name}
            item
            xs={12}
            className="snapshot-instrument cursor-pointer"
            onClick={() => clickInstrument(name)}
          >
            <div className="item">
              <Checkbox className="item-checkbox" selected={isSelected} />
              <div className="item-name">{INSTRUMENT_NAME_MAP[name]}</div>
            </div>
          </Grid>
        );
      })}
      <Grid item xs={12}>
        <button disabled={selectedInstruments.size <= 0} onClick={createSnapshots}>
          {`Take ${selectedInstruments.size > 1 ? 'Screenshots' : 'Screenshot'}`}
        </button>
      </Grid>
      <Grid item xs={12}>
        <span className="warning-item">{warningMessage}</span>
      </Grid>
    </Grid>
  );
};

SnapshotForm.propTypes = {
  instruments: PropTypes.object.isRequired,
  toolbarRef: PropTypes.object.isRequired,
};

export default SnapshotForm;
